/**
 * useDashboardLayout — laadt de per-user widget-layout + welk template de gebruiker
 * volgt. Niet-aangepaste gebruikers (customized=false) renderen het template LIVE,
 * dus een admin die een template opslaat "pusht" het automatisch. Zodra de gebruiker
 * zelf iets wijzigt → customized=true en de eigen indeling wordt bewaard.
 *
 * De registry blijft bron-van-waarheid: geladen layouts worden gereconcileerd.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { WIDGETS, DEFAULT_LAYOUT, LAYOUT_VERSION } from './registry';

const API = `${BACKEND_URL}/Api/dashboard-layout`;
const TPL_API = `${BACKEND_URL}/Api/dashboard-templates`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

let idSeq = 0;
const genId = (type) => `${type}-${Date.now().toString(36)}-${idSeq++}`;

function reconcile(widgets) {
  const seen = new Set();
  return (Array.isArray(widgets) ? widgets : [])
    .map((w) => {
      const meta = WIDGETS[w?.type];
      if (!meta) return null;
      let i = w.i;
      if (!i || seen.has(i)) i = genId(w.type);
      seen.add(i);
      const h = meta.allowedHeights.includes(w.h) ? w.h : meta.defaultH;
      return {
        i, type: w.type,
        w: clamp(Number(w.w) || meta.defaultW, meta.minW, meta.maxW),
        h,
        settings: (w.settings && typeof w.settings === 'object') ? w.settings : {},
      };
    })
    .filter(Boolean);
}

const cloneDefault = () => reconcile(DEFAULT_LAYOUT.widgets.map((w) => ({ ...w, settings: { ...w.settings } })));

export default function useDashboardLayout() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState('standaard');
  const [customized, setCustomized] = useState(false);

  const templatesRef = useRef([]);
  const templateKeyRef = useRef('standaard');
  const customizedRef = useRef(false);
  const saveTimer = useRef(null);
  const loaded = useRef(false);

  const layoutForTemplate = useCallback((key) => {
    const tmpl = templatesRef.current.find((t) => t.key === key);
    if (tmpl?.layout?.widgets) return reconcile(tmpl.layout.widgets);
    return key === 'standaard' ? cloneDefault() : [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tRes, lRes] = await Promise.all([
          authFetch(TPL_API).then((r) => (r.ok ? r.json() : { templates: [] })).catch(() => ({ templates: [] })),
          authFetch(API).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        if (cancelled) return;
        const tmpls = tRes?.templates || [];
        templatesRef.current = tmpls;
        setTemplates(tmpls);

        const key = lRes?.template_key || 'standaard';
        const cust = !!lRes?.customized;
        templateKeyRef.current = key; customizedRef.current = cust;
        setTemplateKey(key); setCustomized(cust);

        const incoming = lRes?.layout?.widgets;
        // Een LEEG array is een geldige keuze (gebruiker heeft alle widgets verwijderd)
        // — dus niet op .length checken, anders komt de template-layout steeds terug.
        const next = (cust && Array.isArray(incoming)) ? reconcile(incoming) : layoutForTemplate(key);
        setWidgets(next);
      } catch {
        if (!cancelled) setWidgets(cloneDefault());
      } finally {
        if (!cancelled) { setLoading(false); loaded.current = true; }
      }
    })();
    return () => { cancelled = true; };
  }, [layoutForTemplate]);

  const persist = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await authFetch(API, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            layout: { version: LAYOUT_VERSION, widgets: next },
            template_key: templateKeyRef.current,
            customized: customizedRef.current,
          }),
        });
        if (!r.ok) throw new Error('save failed');
      } catch {
        toast.error('Indeling niet opgeslagen');
      }
    }, 400);
  }, []);

  // Gebruiker-mutatie: markeert customized=true.
  const commit = useCallback((updater) => {
    setWidgets((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (loaded.current) {
        if (!customizedRef.current) { customizedRef.current = true; setCustomized(true); }
        persist(next);
      }
      return next;
    });
  }, [persist]);

  const reorder = useCallback((next) => commit(next), [commit]);
  const updateWidget = useCallback((i, patch) =>
    commit((prev) => prev.map((w) => (w.i === i ? { ...w, ...patch } : w))), [commit]);
  const removeWidget = useCallback((i) =>
    commit((prev) => prev.filter((w) => w.i !== i)), [commit]);
  const addWidgetAt = useCallback((type, index) => {
    const meta = WIDGETS[type];
    if (!meta) return;
    commit((prev) => {
      const next = [...prev];
      const item = { i: genId(type), type, w: meta.defaultW, h: meta.defaultH, settings: { ...(meta.defaultSettings || {}) } };
      const at = Math.max(0, Math.min(index ?? next.length, next.length));
      next.splice(at, 0, item);
      return next;
    });
  }, [commit]);

  // Template kiezen: neemt de template-indeling over en zet customized=false.
  const applyTemplate = useCallback((key) => {
    const next = layoutForTemplate(key);
    templateKeyRef.current = key; customizedRef.current = false;
    setTemplateKey(key); setCustomized(false);
    setWidgets(next);
    if (loaded.current) persist(next);
  }, [layoutForTemplate, persist]);

  // Reset = terug naar het huidige template (eigen aanpassingen weg).
  const resetToTemplate = useCallback(() => applyTemplate(templateKeyRef.current), [applyTemplate]);

  return {
    widgets, loading, templates, templateKey, customized,
    reorder, updateWidget, removeWidget, addWidgetAt,
    applyTemplate, resetToDefault: resetToTemplate,
  };
}
